/** Minimal ProseMirror-JSON node shape (enough to walk + tag top-level blocks). */
interface PMNode {
  type: string;
  attrs?: Record<string, unknown>;
  content?: PMNode[];
  text?: string;
}

const ATOM_BLOCKS = new Set(['image', 'wikiEmbed', 'checklistProgress', 'horizontalRule']);

function textOf(n: PMNode): string {
  if (typeof n.text === 'string') return n.text;
  return (n.content ?? []).map(textOf).join('');
}

/** A stable identity for a top-level block, so the LCS can tell "same block" from "changed block".
 *  Atom blocks (image/embed/widget/divider) key off their attrs; text blocks off their type + text. */
function blockKey(n: PMNode): string {
  if (ATOM_BLOCKS.has(n.type)) return `${n.type}|${JSON.stringify(n.attrs ?? {})}`;
  return `${n.type}|${textOf(n).trim()}`;
}

/**
 * Produces a MERGED ProseMirror document that renders the change from oldDoc → newDoc inline: unchanged
 * blocks pass through, added blocks (in new, not old) are tagged attrs.diff='add' (green), removed blocks
 * (in old, not new) are re-inserted tagged attrs.diff='del' (red strikethrough). Block-level LCS keyed by
 * {@link blockKey}, so a block whose text changed shows as its old version struck through + the new one
 * highlighted. The diff attr is surfaced by the DiffAttributes extension → a data-diff attribute the CSS
 * styles. Returns newDoc unchanged when there's no previous version.
 */
export function diffContent(oldDoc: unknown, newDoc: unknown): Record<string, unknown> {
  const newObj = (newDoc && typeof newDoc === 'object' ? newDoc : { type: 'doc', content: [] }) as PMNode;
  if (!oldDoc || typeof oldDoc !== 'object') return newObj as unknown as Record<string, unknown>;
  const a = ((oldDoc as PMNode).content ?? []) as PMNode[];
  const b = (newObj.content ?? []) as PMNode[];
  const ak = a.map(blockKey);
  const bk = b.map(blockKey);
  const n = a.length;
  const m = b.length;

  // LCS length table (suffixes), then walk it to interleave same/add/del in reading order.
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) for (let j = m - 1; j >= 0; j--) dp[i][j] = ak[i] === bk[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);

  const tag = (block: PMNode, diff: 'add' | 'del' | null): PMNode => ({ ...block, attrs: { ...(block.attrs ?? {}), diff } });
  const out: PMNode[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (ak[i] === bk[j]) { out.push(tag(b[j], null)); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { out.push(tag(a[i], 'del')); i++; }
    else { out.push(tag(b[j], 'add')); j++; }
  }
  while (i < n) { out.push(tag(a[i], 'del')); i++; }
  while (j < m) { out.push(tag(b[j], 'add')); j++; }

  return { type: 'doc', content: out };
}
